import React, { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, User, Mail, Lock, Check, DollarSign } from 'react-feather';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import SocialLoginButtons from '../../components/auth/SocialLoginButtons';
import { toast } from 'react-toastify';
import SubscriptionService, { SUPPORTED_CURRENCIES } from '../../services/subscription.service';

// Define currency type for type safety
type SupportedCurrency = 'USD' | 'NGN' | 'GHS' | 'KES' | 'ZAR' | 'XOF';
type PlanType = 'basic' | 'premium' | 'ultimate';
type PlanDuration = 'monthly' | 'yearly';

// Define interface for currency price object
interface CurrencyPrices {
  USD: number;
  NGN: number;
  GHS: number;
  KES: number;
  ZAR: number;
  XOF: number;
}

// Define interface for price structure
interface PlanPrice {
  monthly: number | CurrencyPrices;
  yearly: number | CurrencyPrices;
}

// Define interface for plan details
interface PlanDetails {
  name: string;
  description: string;
  features: string[];
  price: PlanPrice;
}

// Define interface for all plans
interface Plans {
  basic: PlanDetails;
  premium: PlanDetails;
  ultimate: PlanDetails;
}

const Register: React.FC = () => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  
  // Get plan info from URL params
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [planType, setPlanType] = useState<PlanType>((queryParams.get('plan') as PlanType) || 'basic');
  const [planDuration, setPlanDuration] = useState<PlanDuration>((queryParams.get('duration') as PlanDuration) || 'monthly');
  const [currency, setCurrency] = useState<SupportedCurrency>((queryParams.get('currency') as SupportedCurrency) || 'NGN');
  const [showPlanSelector, setShowPlanSelector] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const { register } = useAuth();

  // Currency symbols for each supported currency
  const currencySymbols: Record<SupportedCurrency, string> = {
    USD: '$',
    NGN: '₦',
    GHS: 'GH₵',
    KES: 'KSh',
    ZAR: 'R',
    XOF: 'CFA'
  };

  // Plan details with multi-currency pricing
  const [plans, setPlans] = useState<Plans>({
    basic: {
      name: 'Basic',
      description: 'Free plan with essential features',
      features: ['Wedding Website', 'Guest List (up to 100)', 'Basic Gift Registry'],
      price: { monthly: 0, yearly: 0 }
    },
    premium: {
      name: 'Premium',
      description: 'Enhanced features for a perfect day',
      features: ['Custom Domain', 'Unlimited Images', 'Up to 300 Guests', 'Lower Fees (2.5%)'],
      price: { 
        monthly: { 
          USD: 9.99, 
          NGN: 12000, 
          GHS: 80, 
          KES: 1100, 
          ZAR: 160, 
          XOF: 5500 
        },
        yearly: { 
          USD: 95.88, 
          NGN: 115000, 
          GHS: 770, 
          KES: 10500, 
          ZAR: 1530, 
          XOF: 52500 
        }
      }
    },
    ultimate: {
      name: 'Ultimate',
      description: 'Everything you need for your dream wedding',
      features: ['Priority Support', 'Unlimited Guests', 'Lowest Fees (1%)', 'VIP Processing'],
      price: { 
        monthly: { 
          USD: 20, 
          NGN: 24000, 
          GHS: 160, 
          KES: 2200, 
          ZAR: 320, 
          XOF: 11000 
        },
        yearly: { 
          USD: 192, 
          NGN: 230000, 
          GHS: 1540, 
          KES: 21000, 
          ZAR: 3060, 
          XOF: 105000 
        }
      }
    }
  });

  // Fetch pricing from API on component mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await SubscriptionService.getPricing();
        if (response.data && response.data.pricing) {
          // Update plans with pricing from API
          setPlans(prev => ({
            ...prev,
            premium: {
              ...prev.premium,
              price: response.data.pricing.premium
            },
            ultimate: {
              ...prev.ultimate,
              price: response.data.pricing.ultimate
            }
          }));
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
        // Continue with default pricing if API fails
      }
    };

    fetchPricing();
  }, []);

  // Detect user's preferred currency based on browser locale
  useEffect(() => {
    // If currency isn't explicitly set in URL, try to detect it
    if (!queryParams.get('currency')) {
      try {
        const userLocale = navigator.language;
        let detectedCurrency: SupportedCurrency = 'NGN'; // Default
        
        if (userLocale.includes('en-US')) detectedCurrency = 'USD';
        else if (userLocale.includes('en-NG')) detectedCurrency = 'NGN';
        else if (userLocale.includes('en-GH')) detectedCurrency = 'GHS';
        else if (userLocale.includes('en-KE')) detectedCurrency = 'KES';
        else if (userLocale.includes('en-ZA')) detectedCurrency = 'ZAR';
        
        // Only set if it's in our supported currencies
        if (SUPPORTED_CURRENCIES.includes(detectedCurrency)) {
          setCurrency(detectedCurrency as SupportedCurrency);
        }
      } catch (error) {
        console.error('Error detecting currency:', error);
        // Fallback to NGN if detection fails
      }
    }
  }, [queryParams]);

  const formatCurrency = (amount: number, currencyCode: SupportedCurrency): string => {
    try {
      // Use locale based on currency
      let locale = 'en-US';
      if (currencyCode === 'NGN') locale = 'en-NG';
      else if (currencyCode === 'GHS') locale = 'en-GH';
      else if (currencyCode === 'KES') locale = 'en-KE';
      else if (currencyCode === 'ZAR') locale = 'en-ZA';

      // Format based on currency
      if (currencyCode === 'USD') {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount);
      } else {
        // For other currencies, no decimal places
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(amount);
      }
    } catch (error) {
      // Fallback formatting
      const symbol = currencySymbols[currencyCode] || '';
      return `${symbol}${amount.toLocaleString()}`;
    }
  };

  const getPlanPrice = (plan: PlanType, duration: PlanDuration): number => {
    if (plan === 'basic') return 0;
    
    try {
      const planInfo = plans[plan];
      const priceInfo = planInfo.price[duration];
      
      // Check if price is a simple number (for basic plan) or an object with currency keys
      if (typeof priceInfo === 'number') {
        return priceInfo;
      } else {
        // Type assertion to tell TypeScript this is a CurrencyPrices object
        const currencyPrices = priceInfo as CurrencyPrices;
        return currencyPrices[currency];
      }
    } catch (error) {
      console.error('Error getting plan price:', error);
      return 0;
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    
    try {
      setError(null);
      setLoading(true);
      
      console.log('Registering user:', { firstName, lastName, email, planType, planDuration, currency });
      const response = await register(firstName, lastName, email, password, planType, planDuration, currency);
      
      console.log('Registration response:', response);
      
      // Store email for verification
      localStorage.setItem('pendingVerificationEmail', email);
      // Store currency preference
      localStorage.setItem('preferredCurrency', currency);
      
      // If premium plan with payment redirect
      if (response.payment && response.payment.authorizationUrl) {
        // Notify user about redirection to payment
        toast.success('Account created! Redirecting to payment...', {
          position: 'top-right',
          autoClose: 3000,
        });
        
        // Redirect to payment
        window.location.href = response.payment.authorizationUrl;
        return;
      }
      
      // Basic plan flow - show success message
      toast.success('Registration successful! Please check your email to verify your account.', {
        position: 'top-right',
        autoClose: 5000,
      });
      
      // Redirect to verification page
      window.location.href = '/verification-required';
    } catch (err: any) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to create account';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Heart className="h-12 w-12 text-pink-500" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Start planning your dream wedding
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4">
              <Alert
                type="error"
                message={error}
                dismissible
                onDismiss={() => setError(null)}
              />
            </div>
          )}
          
          {/* Plan Selector Card */}
          <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 flex justify-between items-center border-b border-gray-100">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {plans[planType].name} Plan
                </h3>
                <p className="text-sm text-gray-500">
                  {planDuration === 'monthly' ? 'Monthly billing' : 'Annual billing (Save 20%)'}
                </p>
              </div>
              
              <button
                type="button"
                onClick={() => setShowPlanSelector(!showPlanSelector)}
                className="text-sm font-medium text-pink-600 hover:text-pink-700"
              >
                {showPlanSelector ? 'Hide Options' : 'Change Plan'}
              </button>
            </div>
            
            {/* Plan Pricing for Premium/Ultimate */}
            {planType !== 'basic' && (
              <div className="p-4 bg-pink-50 border-b border-pink-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Price:</p>
                    <div className="flex items-baseline">
                      <span className="text-xl font-semibold text-gray-900">
                        {formatCurrency(getPlanPrice(planType, planDuration), currency)}
                      </span>
                      <span className="ml-1 text-sm text-gray-500">
                        {planDuration === 'monthly' ? '/month' : '/year'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Currency: {currency} ({currencySymbols[currency]})
                    </p>
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    {planDuration === 'yearly' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Save 20%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Plan Features */}
            <div className="p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Includes:</h4>
              <ul className="space-y-2">
                {plans[planType].features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Plan Selector Dropdown */}
          {showPlanSelector && (
            <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-inner">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Plan:</label>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.keys(plans) as Array<PlanType>).map((plan) => (
                    <button
                      key={plan}
                      type="button"
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        planType === plan
                          ? plan === 'basic'
                            ? 'bg-gray-200 text-gray-800'
                            : plan === 'premium'
                            ? 'bg-pink-600 text-white'
                            : 'bg-green-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => setPlanType(plan)}
                    >
                      {plans[plan].name}
                    </button>
                  ))}
                </div>
              </div>
              
              {planType !== 'basic' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Billing Period:</label>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          planDuration === 'monthly'
                            ? 'bg-gray-800 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => setPlanDuration('monthly')}
                      >
                        Monthly
                      </button>
                      <button
                        type="button"
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          planDuration === 'yearly'
                            ? 'bg-gray-800 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => setPlanDuration('yearly')}
                      >
                        Yearly (Save 20%)
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency:
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {SUPPORTED_CURRENCIES.map((currencyCode) => (
                        <button
                          key={currencyCode}
                          type="button"
                          className={`px-3 py-2 text-sm font-medium rounded-md flex items-center justify-center ${
                            currency === currencyCode
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => setCurrency(currencyCode as SupportedCurrency)}
                        >
                          <DollarSign className="h-3 w-3 mr-1" />
                          {currencyCode}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <Input
                id="first-name"
                name="first-name"
                type="text"
                autoComplete="given-name"
                required
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                leftIcon={<User className="h-5 w-5 text-gray-400" />}
              />

              <Input
                id="last-name"
                name="last-name"
                type="text"
                autoComplete="family-name"
                required
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                leftIcon={<User className="h-5 w-5 text-gray-400" />}
              />
            </div>

            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
            />

            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
              helperText="Password must be at least 8 characters"
            />

            <Input
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              label="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
              error={password !== confirmPassword && confirmPassword !== '' ? 'Passwords do not match' : undefined}
            />

            {/* Hidden fields for plan data and currency */}
            <input type="hidden" name="planType" value={planType} />
            <input type="hidden" name="planDuration" value={planDuration} />
            <input type="hidden" name="currency" value={currency} />

            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              Create account
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or sign up with
                </span>
              </div>
            </div>

            <SocialLoginButtons 
              className="mt-6" 
              planType={planType}
              planDuration={planDuration}
              currency={currency}
            />

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-pink-600 hover:text-pink-500">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;